import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Bilingual content ────────────────────────────────────────────────────────
const TEXTS = {
  en: {
    nav: {
      features: 'Features', howItWorks: 'How it works', pricing: 'Pricing', faq: 'FAQ',
      login: 'Log in', startFree: 'Start free', dashboard: 'Dashboard',
    },
    hero: {
      badge: 'Trusted by 1,200+ families',
      title1: 'Stop stressing about',
      title2: "what's for dinner",
      subtitle: 'MealPlanner generates a full week of personalized meals in seconds, builds your shopping list automatically, and keeps your whole family in sync — all in one place.',
      cta: 'Start Planning Free',
      secondary: 'See how it works',
      note: 'No credit card required · Free plan available forever · Cancel anytime',
      weekLabel: 'Week of Feb 24 – Mar 2',
      weekBadge: '7 days planned ✓',
      shoppingReady: 'Shopping list ready',
      sharedFamily: 'Shared with family',
      generatedIn: 'Generated in 0.3s ⚡',
    },
    stats: [
      { value: '1,200+', label: 'Active families' },
      { value: '50k+', label: 'Menus generated' },
      { value: '4.8 ★', label: 'Average rating' },
      { value: '2 hrs', label: 'Saved per week' },
    ],
    problem: {
      title: 'Sound familiar?',
      subtitle: "Every week, the same exhausting cycle. We've all been there.",
      cards: [
        { title: 'The 6pm panic', desc: "It's dinnertime and you have no idea what to cook. Again. You end up ordering takeout." },
        { title: 'Supermarket chaos', desc: "You wander the aisles without a plan, buy random things, then realize you're missing half the ingredients." },
        { title: 'Food waste guilt', desc: 'Vegetables rotting in the fridge. Half-used ingredients. Money down the drain every single week.' },
        { title: 'Same 5 dishes', desc: "You always cook the same rotation. Everyone's bored, but planning something different feels like too much effort." },
      ],
    },
    solution: {
      badge: 'The solution',
      title1: 'One click. A full week',
      title2: 'of meals, sorted.',
      subtitle: "MealPlanner uses your personal recipe collection to generate a balanced, varied weekly menu — making sure you never repeat the same dish two days in a row. Then it automatically builds your shopping list.",
      bullets: [
        'Personalized to your recipes and preferences',
        'Smart variety — no repeated dishes or food categories on consecutive days',
        'Shopping list auto-generated and shareable with your family',
        'Works in English and Spanish',
      ],
      shoppingTitle: 'Shopping List — This Week',
      vegSection: 'Vegetables & Fruit',
      meatSection: 'Meat & Fish',
      shoppingItems: [
        { name: 'Tomatoes (500g)', checked: true },
        { name: 'Onions (3 units)', checked: false },
        { name: 'Garlic (1 head)', checked: true },
        { name: 'Baby spinach (200g)', checked: false },
      ],
      meatItems: ['Chicken breast (600g)', 'Salmon fillet (400g)'],
      checkedCount: '4 of 18 items checked',
      sharedFamily: 'Shared with family ✓',
    },
    features: {
      badge: 'Features',
      title: 'Everything you need to plan smarter',
      subtitle: 'Designed for real families who want to eat well without spending hours planning.',
      cards: [
        { title: 'Instant menu generation', desc: 'Click "Generate" and get a complete, balanced weekly menu in under a second — built from your own recipe library.' },
        { title: 'Automatic shopping list', desc: 'Every menu creates a grouped shopping list automatically. Check items off as you shop — state is saved even when you close the app.' },
        { title: 'Family sharing', desc: 'Share your weekly menu and shopping list with your partner or family. Everyone stays in sync — from the kitchen to the store.' },
        { title: 'Smart variety rules', desc: 'Never eat the same dish or the same food category two days in a row. Our algorithm ensures true variety without you thinking about it.' },
        { title: 'Beautiful recipe cards', desc: 'Add your recipes with photos, ingredients, and instructions. Automatic image search keeps your library looking great.' },
        { title: 'Fully customizable', desc: "Drag and swap any meal in the menu. Add your own recipes, set categories, and fine-tune generation rules to fit your family's tastes." },
      ],
    },
    howItWorks: {
      badge: 'How it works',
      title: 'Up and running in minutes',
      subtitle: 'No learning curve. No complicated setup. Just meal planning that actually works.',
      steps: [
        { title: 'Add your recipes', desc: "Enter the meals your family loves — or start with our suggestions. Add ingredients, instructions, and let MealPlanner handle the rest. It only takes a few minutes to build your library." },
        { title: 'Generate your menu', desc: 'Pick the week and hit "Generate". MealPlanner builds a balanced 7-day menu from your recipes, ensuring variety — no consecutive repeats. Swap any meal with a single click if you want to make changes.' },
        { title: 'Shop & enjoy', desc: 'Your shopping list is ready automatically — grouped by category, shareable with anyone. Check off items as you shop. Then just cook and enjoy dinner without the daily stress.' },
      ],
    },
    testimonials: {
      badge: 'Testimonials',
      title: 'Families love MealPlanner',
      cards: [
        { text: '"We used to spend 30 minutes every Sunday figuring out meals. Now I click generate and we\'re done. The shopping list feature alone saves us at least an hour a week."', name: 'Sarah M.', role: 'Mum of 3, London' },
        { text: '"As someone who\'s terrible at planning, this app changed everything. I\'ve tried 4 other meal planners and none of them were as simple and fast. Absolutely worth it."', name: 'Javier R.', role: 'Working dad, Madrid' },
        { text: '"My husband and I share the list and it\'s a game changer. We do the shopping separately and never buy duplicates. The \'avoid consecutive\' feature is brilliant — real variety every week."', name: 'Ana C.', role: 'Home cook, Barcelona' },
      ],
    },
    pricing: {
      badge: 'Pricing',
      title: 'Simple, honest pricing',
      subtitle: 'Start free. Upgrade only when you need more. No hidden fees, no surprises.',
      monthly: 'Monthly',
      yearly: 'Yearly',
      savePercent: '(save 20%)',
      mostPopular: 'MOST POPULAR',
      perMonth: '/ month',
      billedYearlyPro: 'billed as £57.50 / year',
      billedYearlyPremium: 'billed as £95.90 / year',
      note: 'Start with the free plan, no credit card required. Upgrade anytime. Cancel whenever you want.',
      plans: [
        {
          name: 'Free', subtitle: 'Perfect for getting started', price: '£0', yearlyPrice: '£0',
          cta: 'Get started free',
          features: [
            { text: 'Up to 20 recipes', ok: true }, { text: 'Up to 5 weekly menus', ok: true },
            { text: 'Automatic shopping list', ok: true }, { text: 'EN & ES support', ok: true },
            { text: 'Family sharing', ok: false }, { text: 'Recipe images (auto-search)', ok: false },
          ],
        },
        {
          name: 'Pro', subtitle: 'For families who plan seriously', price: '£5.99', yearlyPrice: '£4.79',
          cta: 'Get Pro', popular: true,
          features: [
            { text: 'Unlimited recipes', ok: true }, { text: 'Unlimited weekly menus', ok: true },
            { text: 'Automatic shopping list', ok: true }, { text: 'Family sharing', ok: true },
            { text: 'Recipe images (auto-search)', ok: true }, { text: 'EN & ES support', ok: true },
          ],
        },
        {
          name: 'Premium', subtitle: 'For power users & large families', price: '£9.99', yearlyPrice: '£7.99',
          cta: 'Get Premium',
          features: [
            { text: 'Everything in Pro', ok: true }, { text: 'Priority support', ok: true },
            { text: 'Early access to new features', ok: true },
            { text: 'Advanced food category rules', ok: true },
            { text: 'Dedicated account features', ok: true },
          ],
        },
      ],
    },
    faq: {
      badge: 'FAQ',
      title: 'Frequently asked questions',
      subtitle: 'Everything you need to know before getting started.',
      items: [
        { q: 'Do I need a credit card to sign up?', a: "No. The Free plan requires no credit card — ever. For Pro and Premium you'll only be asked for payment details when you're ready to upgrade." },
        { q: 'How does the menu generation work?', a: "MealPlanner draws from your personal recipe library to fill each day's lunch and dinner. It applies smart rules to avoid repeating the same dish or food category on consecutive days. You can swap any meal manually after generation." },
        { q: 'Can I share my menu with my partner?', a: "Yes. Pro and Premium plans include family sharing. You can share your weekly menu and shopping list with anyone — no account required for viewers. They can even check off shopping items." },
        { q: 'Can I edit the generated menu?', a: 'Absolutely. After generating, you can swap any meal slot with a different recipe from your library. Changes are saved automatically.' },
        { q: 'What languages does MealPlanner support?', a: 'MealPlanner is fully available in English and Spanish (Español). The interface, emails, and all features work in both languages.' },
        { q: 'Can I cancel my subscription at any time?', a: "Yes, always. Cancel from your account settings at any time. You'll keep access until the end of your current billing period. No cancellation fees." },
      ],
    },
    cta: {
      title1: 'Ready to end the',
      title2: 'dinner planning stress?',
      subtitle: "Join 1,200+ families who've taken back their evenings. Start free today — no credit card, no commitment.",
      primaryBtn: 'Start Planning Free',
      secondaryBtn: 'Already have an account? Log in',
      secondaryBtnAuth: 'Go to dashboard',
    },
    footer: {
      tagline: 'Plan your week, enjoy your table. The simplest way to take stress out of daily meal planning.',
      product: 'Product', account: 'Account', legal: 'Legal',
      signUp: 'Sign up free', login: 'Log in', viewPricing: 'View pricing', privacy: 'Privacy Policy',
      copyright: '© 2026 MealPlanner. All rights reserved.',
    },
  },

  es: {
    nav: {
      features: 'Funcionalidades', howItWorks: 'Cómo funciona', pricing: 'Precios', faq: 'FAQ',
      login: 'Iniciar sesión', startFree: 'Empezar gratis', dashboard: 'Panel',
    },
    hero: {
      badge: 'Con la confianza de más de 1.200 familias',
      title1: 'Deja de estresarte con',
      title2: 'qué comer cada día',
      subtitle: 'MealPlanner genera una semana completa de comidas personalizadas en segundos, crea tu lista de la compra automáticamente y mantiene a toda tu familia sincronizada.',
      cta: 'Empezar a planificar gratis',
      secondary: 'Ver cómo funciona',
      note: 'Sin tarjeta de crédito · Plan gratuito disponible siempre · Cancela cuando quieras',
      weekLabel: 'Semana del 24 feb – 2 mar',
      weekBadge: '7 días planificados ✓',
      shoppingReady: 'Lista de compra lista',
      sharedFamily: 'Compartido con la familia',
      generatedIn: 'Generado en 0,3 s ⚡',
    },
    stats: [
      { value: '1.200+', label: 'Familias activas' },
      { value: '50k+', label: 'Menús generados' },
      { value: '4,8 ★', label: 'Valoración media' },
      { value: '2 h', label: 'Ahorradas por semana' },
    ],
    problem: {
      title: '¿Te suena familiar?',
      subtitle: 'Cada semana, el mismo ciclo agotador. Todos lo hemos vivido.',
      cards: [
        { title: 'El pánico de las 8', desc: 'Es la hora de cenar y no tienes ni idea de qué cocinar. Otra vez. Acabas pidiendo comida a domicilio.' },
        { title: 'Caos en el súper', desc: 'Deambulas por los pasillos sin plan, compras cosas al azar y al llegar a casa te das cuenta de que te faltan la mitad de los ingredientes.' },
        { title: 'Culpa por desperdiciar', desc: 'Verduras pudriéndose en la nevera. Ingredientes a medias. Dinero tirado a la basura semana tras semana.' },
        { title: 'Siempre los mismos 5 platos', desc: 'Siempre cocinas lo mismo. Toda la familia está aburrida, pero planificar algo diferente parece demasiado esfuerzo.' },
      ],
    },
    solution: {
      badge: 'La solución',
      title1: 'Un clic. Una semana entera',
      title2: 'de comidas, resuelta.',
      subtitle: 'MealPlanner usa tu colección personal de recetas para generar un menú semanal variado y equilibrado, asegurándose de que nunca repitas el mismo plato dos días seguidos. Y luego crea la lista de la compra automáticamente.',
      bullets: [
        'Personalizado para tus recetas y preferencias',
        'Variedad inteligente — sin platos ni categorías repetidas en días consecutivos',
        'Lista de compra generada automáticamente y compartible con tu familia',
        'Disponible en español e inglés',
      ],
      shoppingTitle: 'Lista de la compra — Esta semana',
      vegSection: 'Verduras y Fruta',
      meatSection: 'Carne y Pescado',
      shoppingItems: [
        { name: 'Tomates (500 g)', checked: true },
        { name: 'Cebollas (3 unidades)', checked: false },
        { name: 'Ajo (1 cabeza)', checked: true },
        { name: 'Espinacas baby (200 g)', checked: false },
      ],
      meatItems: ['Pechuga de pollo (600 g)', 'Filete de salmón (400 g)'],
      checkedCount: '4 de 18 artículos marcados',
      sharedFamily: 'Compartido con la familia ✓',
    },
    features: {
      badge: 'Funcionalidades',
      title: 'Todo lo que necesitas para planificar mejor',
      subtitle: 'Diseñado para familias reales que quieren comer bien sin perder horas planificando.',
      cards: [
        { title: 'Generación de menú al instante', desc: 'Pulsa "Generar" y obtén un menú semanal completo y equilibrado en menos de un segundo, creado con tus propias recetas.' },
        { title: 'Lista de compra automática', desc: 'Cada menú genera automáticamente una lista de compra agrupada. Marca los artículos mientras compras — el estado se guarda aunque cierres la app.' },
        { title: 'Compartir en familia', desc: 'Comparte tu menú semanal y la lista de compra con tu pareja o familia. Todos sincronizados — desde la cocina hasta el súper.' },
        { title: 'Reglas de variedad inteligente', desc: 'Nunca comas el mismo plato ni la misma categoría de comida dos días seguidos. Nuestro algoritmo garantiza variedad real sin que tengas que pensar en ello.' },
        { title: 'Fichas de recetas bonitas', desc: 'Añade tus recetas con fotos, ingredientes e instrucciones. La búsqueda automática de imágenes mantiene tu biblioteca con un aspecto estupendo.' },
        { title: 'Totalmente personalizable', desc: 'Arrastra e intercambia cualquier comida del menú. Añade tus recetas, configura categorías y ajusta las reglas de generación a los gustos de tu familia.' },
      ],
    },
    howItWorks: {
      badge: 'Cómo funciona',
      title: 'En marcha en minutos',
      subtitle: 'Sin curva de aprendizaje. Sin configuración complicada. Solo planificación de comidas que funciona de verdad.',
      steps: [
        { title: 'Añade tus recetas', desc: 'Introduce los platos que le gustan a tu familia — o empieza con nuestras sugerencias. Añade ingredientes e instrucciones y deja que MealPlanner se encargue del resto. Solo tardarás unos minutos en construir tu biblioteca.' },
        { title: 'Genera tu menú', desc: 'Elige la semana y pulsa "Generar". MealPlanner crea un menú de 7 días equilibrado con tus recetas, garantizando variedad. Intercambia cualquier comida con un solo clic si quieres hacer cambios.' },
        { title: 'Compra y disfruta', desc: 'Tu lista de compra está lista automáticamente — agrupada por categoría y compartible con quien quieras. Marca los artículos mientras compras. Luego cocina y disfruta de la cena sin el estrés diario.' },
      ],
    },
    testimonials: {
      badge: 'Opiniones',
      title: 'Las familias adoran MealPlanner',
      cards: [
        { text: '"Antes pasábamos 30 minutos cada domingo pensando en las comidas. Ahora pulso generar y listo. La función de lista de la compra por sí sola nos ahorra al menos una hora a la semana."', name: 'Sarah M.', role: 'Madre de 3, Londres' },
        { text: '"Como alguien a quien se le da fatal planificar, esta app lo cambió todo. He probado otros 4 planificadores de comidas y ninguno era tan sencillo y rápido. Totalmente recomendable."', name: 'Javier R.', role: 'Padre trabajador, Madrid' },
        { text: '"Mi marido y yo compartimos la lista y es un cambio radical. Hacemos la compra por separado y nunca compramos duplicados. La función \'evitar consecutivos\' es brillante — variedad real cada semana."', name: 'Ana C.', role: 'Cocinera en casa, Barcelona' },
      ],
    },
    pricing: {
      badge: 'Precios',
      title: 'Precios simples y transparentes',
      subtitle: 'Empieza gratis. Actualiza solo cuando necesites más. Sin tarifas ocultas, sin sorpresas.',
      monthly: 'Mensual',
      yearly: 'Anual',
      savePercent: '(ahorra 20%)',
      mostPopular: 'MÁS POPULAR',
      perMonth: '/ mes',
      billedYearlyPro: 'facturado como £57,50 / año',
      billedYearlyPremium: 'facturado como £95,90 / año',
      note: 'Empieza con el plan gratuito, sin tarjeta. Actualiza cuando quieras. Cancela en cualquier momento.',
      plans: [
        {
          name: 'Gratis', subtitle: 'Perfecto para empezar', price: '£0', yearlyPrice: '£0',
          cta: 'Empezar gratis',
          features: [
            { text: 'Hasta 20 recetas', ok: true }, { text: 'Hasta 5 menús semanales', ok: true },
            { text: 'Lista de compra automática', ok: true }, { text: 'ES e EN', ok: true },
            { text: 'Compartir en familia', ok: false }, { text: 'Imágenes de recetas (automático)', ok: false },
          ],
        },
        {
          name: 'Pro', subtitle: 'Para familias que planifican en serio', price: '£5,99', yearlyPrice: '£4,79',
          cta: 'Obtener Pro', popular: true,
          features: [
            { text: 'Recetas ilimitadas', ok: true }, { text: 'Menús semanales ilimitados', ok: true },
            { text: 'Lista de compra automática', ok: true }, { text: 'Compartir en familia', ok: true },
            { text: 'Imágenes de recetas (automático)', ok: true }, { text: 'ES e EN', ok: true },
          ],
        },
        {
          name: 'Premium', subtitle: 'Para usuarios avanzados y familias grandes', price: '£9,99', yearlyPrice: '£7,99',
          cta: 'Obtener Premium',
          features: [
            { text: 'Todo lo del plan Pro', ok: true }, { text: 'Soporte prioritario', ok: true },
            { text: 'Acceso anticipado a novedades', ok: true },
            { text: 'Reglas avanzadas de categorías', ok: true },
            { text: 'Funciones de cuenta dedicadas', ok: true },
          ],
        },
      ],
    },
    faq: {
      badge: 'FAQ',
      title: 'Preguntas frecuentes',
      subtitle: 'Todo lo que necesitas saber antes de empezar.',
      items: [
        { q: '¿Necesito una tarjeta de crédito para registrarme?', a: 'No. El plan Gratis no requiere tarjeta nunca. Para Pro y Premium solo se te pedirá información de pago cuando estés listo para actualizar.' },
        { q: '¿Cómo funciona la generación de menús?', a: 'MealPlanner toma recetas de tu biblioteca personal para completar el almuerzo y la cena de cada día. Aplica reglas inteligentes para evitar repetir el mismo plato o la misma categoría en días consecutivos. Puedes cambiar cualquier comida manualmente después de generar.' },
        { q: '¿Puedo compartir mi menú con mi pareja?', a: 'Sí. Los planes Pro y Premium incluyen compartir en familia. Puedes compartir tu menú semanal y la lista de compra con cualquier persona — sin necesidad de cuenta para quienes lo ven.' },
        { q: '¿Puedo editar el menú generado?', a: 'Por supuesto. Después de generar, puedes cambiar cualquier franja horaria por otra receta de tu biblioteca. Los cambios se guardan automáticamente.' },
        { q: '¿Qué idiomas soporta MealPlanner?', a: 'MealPlanner está completamente disponible en español e inglés. La interfaz, los correos y todas las funciones funcionan en ambos idiomas.' },
        { q: '¿Puedo cancelar mi suscripción en cualquier momento?', a: 'Sí, siempre. Cancela desde los ajustes de tu cuenta cuando quieras. Mantendrás el acceso hasta el final del período de facturación actual. Sin penalizaciones.' },
      ],
    },
    cta: {
      title1: '¿Listo para acabar con el estrés de',
      title2: 'planificar las comidas?',
      subtitle: 'Únete a más de 1.200 familias que han recuperado sus tardes. Empieza gratis hoy — sin tarjeta, sin compromiso.',
      primaryBtn: 'Empezar a planificar gratis',
      secondaryBtn: '¿Ya tienes cuenta? Inicia sesión',
      secondaryBtnAuth: 'Ir al panel',
    },
    footer: {
      tagline: 'Planifica tu semana, disfruta tu mesa. La forma más sencilla de acabar con el estrés de planificar las comidas a diario.',
      product: 'Producto', account: 'Cuenta', legal: 'Legal',
      signUp: 'Registrarse gratis', login: 'Iniciar sesión', viewPricing: 'Ver precios', privacy: 'Política de Privacidad',
      copyright: '© 2026 MealPlanner. Todos los derechos reservados.',
    },
  },
};

// ── Small reusable pieces ────────────────────────────────────────────────────
const Logo = ({ dark = false }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill="none" className="w-8 h-8 flex-shrink-0">
    <circle cx="24" cy="24" r="20" fill={dark ? '#2d2d2d' : '#FDFBF7'} stroke="#D96C48" strokeWidth="2"/>
    <circle cx="24" cy="24" r="14" fill="none" stroke={dark ? '#444' : '#E8DDD4'} strokeWidth="1"/>
    <path d="M14 12V20M14 20V36M14 20H16M12 12V18H16V12" stroke="#6B8E6B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M34 12V36M34 12C36 14 36 18 34 20" stroke="#6B8E6B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M20 24L23 27L28 21" stroke="#D96C48" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CheckMark = ({ color = '#6B8E6B' }) => (
  <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" style={{ color }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
  </svg>
);

const XMark = () => (
  <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
  </svg>
);

const StarIcon = () => (
  <svg className="w-4 h-4" style={{ fill: '#facc15' }} viewBox="0 0 24 24">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);

const fade = { opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' };
const fadeDelay = (s) => ({ ...fade, transitionDelay: `${s}s` });

// ── Main component ───────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { language, setLanguage, t } = useI18n();
  const [isYearly, setIsYearly] = useState(false);
  const [navShadow, setNavShadow] = useState(false);
  const [loadingCheckout, setLoadingCheckout] = useState(null);
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [config, setConfig] = useState({
    free_max_recipes: 20,
    free_max_menus: 5,
    pro_monthly_price: 5.99,
    premium_monthly_price: 9.99,
    pro_yearly_discount: 20,
    premium_yearly_discount: 20,
  });

  const T = TEXTS[language] || TEXTS.en;
  const goToCTA = () => navigate(isAuthenticated ? '/dashboard' : '/login');
  const isSubscriber = user?.plan === 'pro' || user?.plan === 'premium';

  const handleSubscribe = async (tier) => {
    if (!isAuthenticated) { navigate('/login'); return; }
    setLoadingCheckout(tier);
    try {
      const response = await fetch(`${API}/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tier, plan_type: isYearly ? 'yearly' : 'monthly', origin_url: window.location.origin }),
      });
      const data = await response.json().catch(() => ({ detail: t('errors.generic') }));
      if (response.ok) { window.location.href = data.url; }
      else { toast.error(data.detail === 'payment_failed' ? t('errors.paymentFailed') : t('errors.generic')); }
    } catch { toast.error(t('errors.generic')); }
    finally { setLoadingCheckout(null); }
  };

  const handleManageSubscription = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    setManagingSubscription(true);
    try {
      const response = await fetch(`${API}/payments/create-portal-session`, { method: 'POST', credentials: 'include' });
      if (response.ok) { const data = await response.json(); window.location.href = data.url; }
      else { toast.error(t('errors.generic')); }
    } catch { toast.error(t('errors.generic')); }
    finally { setManagingSubscription(false); }
  };

  const handleFreePlan = () => navigate(isAuthenticated ? '/dashboard' : '/login');

  // Same yearly price calculation as PricingPage
  const proYearlyPrice = (config.pro_monthly_price * 12) * (1 - (config.pro_yearly_discount || 0) / 100);
  const premiumYearlyPrice = (config.premium_monthly_price * 12) * (1 - (config.premium_yearly_discount || 0) / 100);

  // Fetch public config (same as PricingPage)
  useEffect(() => {
    fetch(`${API}/config/public`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setConfig(data); })
      .catch(() => {});
  }, []);

  // Nav shadow
  useEffect(() => {
    const onScroll = () => setNavShadow(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Scroll fade-in
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.style.opacity = '1';
          e.target.style.transform = 'translateY(0)';
        }
      }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.lp-fade').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [language]); // re-run when language changes so newly rendered elements are observed

  return (
    <div className="font-inter text-gray-800 bg-white antialiased">

      {/* ── NAV ── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100 transition-shadow duration-300 ${navShadow ? 'shadow-sm' : ''}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <a href="#" className="flex items-center gap-2.5 font-bold text-gray-900 text-lg">
              <Logo />
              MealPlanner
            </a>

            {/* Desktop links */}
            <div className="hidden md:flex items-center gap-7">
              <a href="#features" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">{T.nav.features}</a>
              <a href="#how-it-works" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">{T.nav.howItWorks}</a>
              <a href="#pricing" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">{T.nav.pricing}</a>
              <a href="#faq" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">{T.nav.faq}</a>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Language toggle */}
              <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => setLanguage('en')}
                  className={`px-3 py-2 transition-colors cursor-pointer ${language === 'en' ? 'text-white' : 'text-gray-500 bg-white'}`}
                  style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', ...(language === 'en' ? { background: '#D96C48' } : {}) }}
                >
                  EN
                </button>
                <button
                  type="button"
                  onClick={() => setLanguage('es')}
                  className={`px-3 py-2 transition-colors cursor-pointer ${language === 'es' ? 'text-white' : 'text-gray-500 bg-white'}`}
                  style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', ...(language === 'es' ? { background: '#D96C48' } : {}) }}
                >
                  ES
                </button>
              </div>

              <button onClick={goToCTA} className="hidden sm:inline-flex text-sm text-gray-600 hover:text-gray-900 transition-colors font-medium">
                {isAuthenticated ? T.nav.dashboard : T.nav.login}
              </button>
              <button onClick={goToCTA} className="inline-flex items-center gap-1.5 text-white text-sm font-semibold px-3 sm:px-4 py-2 rounded-lg hover:opacity-90 transition-opacity" style={{ background: '#D96C48' }}>
                <span className="hidden sm:inline">{T.nav.startFree}</span>
                <span className="sm:hidden">→</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden" style={{ background: 'linear-gradient(135deg,#fdf4f0 0%,#fdfbf7 50%,#f0f5f0 100%)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full mb-6" style={{ background: '#fae5db', color: '#c4552e' }}>
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
              {T.hero.badge}
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-gray-900 leading-tight tracking-tight mb-6">
              {T.hero.title1}<br className="hidden sm:block" />
              <span style={{ color: '#D96C48' }}> {T.hero.title2}</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed">{T.hero.subtitle}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <button onClick={goToCTA} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-white font-bold px-8 py-4 rounded-xl text-base shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200" style={{ background: 'linear-gradient(135deg,#D96C48 0%,#c4552e 100%)' }}>
                {T.hero.cta}
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
              </button>
              <a href="#how-it-works" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-gray-700 font-semibold px-8 py-4 rounded-xl text-base border border-gray-200 hover:border-orange-300 transition-all duration-200">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="10,8 16,12 10,16 10,8"/></svg>
                {T.hero.secondary}
              </a>
            </div>
            <p className="text-xs text-gray-400">{T.hero.note}</p>
          </div>

          {/* Mock weekly menu */}
          <div className="mt-16 max-w-3xl mx-auto lp-fade" style={fade}>
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
              <div className="px-6 py-4 flex items-center justify-between" style={{ background: '#D96C48' }}>
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  <span className="text-white font-semibold text-sm">{T.hero.weekLabel}</span>
                </div>
                <span className="text-white text-xs px-2.5 py-1 rounded-full font-medium" style={{ background: 'rgba(255,255,255,0.2)' }}>{T.hero.weekBadge}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[480px]">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      {(language === 'es' ? ['LUN','MAR','MIÉ','JUE','VIE','SÁB','DOM'] : ['MON','TUE','WED','THU','FRI','SAT','SUN']).map((d, i) => (
                        <th key={d} className={`py-2 text-center font-semibold ${i === 5 ? 'text-terracotta bg-orange-50' : 'text-gray-500'}`}>{d}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-100">
                      {(language === 'es'
                        ? ['Pasta boloñesa','Salmón a la plancha','Pollo salteado','Lentejas','Tacos de ternera','Pizza vegetal','Ensalada griega']
                        : ['Pasta Bolognese','Grilled Salmon','Chicken Stir Fry','Lentil Soup','Beef Tacos','Veggie Pizza','Greek Salad']
                      ).map((meal, i) => (
                        <td key={i} className={`p-2 text-center ${i === 5 ? 'bg-orange-50/30' : ''}`}>
                          <div className="text-gray-400 mb-1">{language === 'es' ? 'Almuerzo' : 'Lunch'}</div>
                          <div className={`${i % 2 === 0 ? 'bg-orange-50' : 'bg-green-50'} rounded-lg p-1.5 text-gray-700 leading-tight`}>{meal}</div>
                        </td>
                      ))}
                    </tr>
                    <tr>
                      {(language === 'es'
                        ? ['Sopa de tomate','Arroz tres delicias','Gazpacho','Verduras a la plancha','Ensalada César','Risotto de champiñones','Wrap de pavo']
                        : ['Tomato Soup','Egg Fried Rice','Gazpacho','Grilled Veg','Caesar Salad','Mushroom Risotto','Turkey Wrap']
                      ).map((meal, i) => (
                        <td key={i} className={`p-2 text-center ${i === 5 ? 'bg-orange-50/30' : ''}`}>
                          <div className="text-gray-400 mb-1">{language === 'es' ? 'Cena' : 'Dinner'}</div>
                          <div className="bg-purple-50 rounded-lg p-1.5 text-gray-700 leading-tight">{meal}</div>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-sage">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/></svg>
                  {T.hero.shoppingReady}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-sage">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                  {T.hero.sharedFamily}
                </span>
                <span className="ml-auto text-xs font-semibold" style={{ color: '#D96C48' }}>{T.hero.generatedIn}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SOCIAL PROOF BAR ── */}
      <section className="py-8 border-y border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            {T.stats.map(({ value, label }, i) => (
              <React.Fragment key={label}>
                {i > 0 && <div className="hidden sm:block w-px h-10 bg-gray-200" />}
                <div className="text-center">
                  <div className="text-2xl font-extrabold text-gray-900">{value}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{label}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROBLEM ── */}
      <section className="py-20 md:py-28 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 lp-fade" style={fade}>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">{T.problem.title}</h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">{T.problem.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { iconPath: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', iconColor: '#ef4444', bg: '#fee2e2', border: '#fecaca' },
              { iconPath: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z', iconColor: '#f97316', bg: '#ffedd5', border: '#fed7aa' },
              { iconPath: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16', iconColor: '#eab308', bg: '#fef9c3', border: '#fef08a' },
              { iconPath: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15', iconColor: '#a855f7', bg: '#f3e8ff', border: '#e9d5ff' },
            ].map((icon, i) => (
              <div key={i} className="lp-fade bg-white rounded-2xl p-6 shadow-sm" style={{ ...fadeDelay(i * 0.1), border: `1px solid ${icon.border}` }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: icon.bg }}>
                  <svg className="w-5 h-5" style={{ color: icon.iconColor }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d={icon.iconPath}/>
                  </svg>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{T.problem.cards[i].title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{T.problem.cards[i].desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SOLUTION ── */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="lp-fade" style={fade}>
              <div className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full mb-6" style={{ background: '#fae5db', color: '#c4552e' }}>{T.solution.badge}</div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6 leading-tight">{T.solution.title1}<br />{T.solution.title2}</h2>
              <p className="text-gray-500 text-lg mb-8 leading-relaxed">{T.solution.subtitle}</p>
              <ul className="space-y-4">
                {T.solution.bullets.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5" style={{ background: '#fae5db' }}>
                      <svg className="w-3.5 h-3.5" style={{ color: '#D96C48' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                    </span>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Shopping list mockup */}
            <div className="lp-fade" style={fadeDelay(0.15)}>
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 flex items-center gap-3" style={{ background: '#6B8E6B' }}>
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
                  <span className="text-white font-semibold text-sm">{T.solution.shoppingTitle}</span>
                </div>
                <div className="p-5 space-y-1">
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{T.solution.vegSection}</div>
                  {T.solution.shoppingItems.map((item) => (
                    <div key={item.name} className="flex items-center gap-3 py-2 border-b border-gray-50">
                      <div className="w-5 h-5 rounded flex-shrink-0 flex items-center justify-center" style={item.checked ? { background: '#fae5db' } : { border: '2px solid #e5e7eb' }}>
                        {item.checked && <svg className="w-3 h-3" style={{ color: '#D96C48' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>}
                      </div>
                      <span className={`text-sm ${item.checked ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{item.name}</span>
                    </div>
                  ))}
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-4 mb-3">{T.solution.meatSection}</div>
                  {T.solution.meatItems.map((name) => (
                    <div key={name} className="flex items-center gap-3 py-2 border-b border-gray-50">
                      <div className="w-5 h-5 rounded flex-shrink-0" style={{ border: '2px solid #e5e7eb' }}></div>
                      <span className="text-sm text-gray-700">{name}</span>
                    </div>
                  ))}
                </div>
                <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-gray-500">{T.solution.checkedCount}</span>
                  <span className="text-xs font-semibold text-sage">{T.solution.sharedFamily}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" className="py-20 md:py-28 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 lp-fade" style={fade}>
            <div className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full mb-6" style={{ background: '#fae5db', color: '#c4552e' }}>{T.features.badge}</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">{T.features.title}</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">{T.features.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { iconBg:'#fae5db', iconColor:'#D96C48', iconPath:'M13 10V3L4 14h7v7l9-11h-7z' },
              { iconBg:'#fae5db', iconColor:'#D96C48', iconPath:'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
              { iconBg:'rgba(107,142,107,0.15)', iconColor:'#6B8E6B', iconPath:'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
              { iconBg:'#ede9fe', iconColor:'#7c3aed', iconPath:'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z' },
              { iconBg:'#dbeafe', iconColor:'#2563eb', iconPath:'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
              { iconBg:'#dcfce7', iconColor:'#16a34a', iconPath:'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' },
            ].map((icon, i) => (
              <div key={i} className="lp-fade bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:-translate-y-1 hover:shadow-lift transition-all duration-200" style={fadeDelay((i % 3) * 0.1)}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: icon.iconBg }}>
                  <svg className="w-6 h-6" style={{ color: icon.iconColor }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d={icon.iconPath}/></svg>
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">{T.features.cards[i].title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{T.features.cards[i].desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 lp-fade" style={fade}>
            <div className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full mb-6" style={{ background: '#fae5db', color: '#c4552e' }}>{T.howItWorks.badge}</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">{T.howItWorks.title}</h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">{T.howItWorks.subtitle}</p>
          </div>
          <div className="relative max-w-3xl mx-auto">
            <div className="hidden md:block absolute left-9 top-16 bottom-16 w-0.5" style={{ background: 'linear-gradient(to bottom,#D96C48,transparent)' }}></div>
            {(['#D96C48','#e4815a','#eda88a']).map((color, i) => (
              <div key={i} className="lp-fade flex flex-col md:flex-row gap-6 mb-10" style={fadeDelay(i * 0.15)}>
                <div className="flex-shrink-0">
                  <div className="w-[4.5rem] h-[4.5rem] text-white rounded-2xl flex items-center justify-center font-extrabold text-xl shadow-lg" style={{ background: color }}>{i + 1}</div>
                </div>
                <div className="md:pl-8 pt-2">
                  <h3 className="font-bold text-gray-900 text-xl mb-2">{T.howItWorks.steps[i].title}</h3>
                  <p className="text-gray-500 leading-relaxed">{T.howItWorks.steps[i].desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-20 md:py-28 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 lp-fade" style={fade}>
            <div className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full mb-6" style={{ background: '#fae5db', color: '#c4552e' }}>{T.testimonials.badge}</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">{T.testimonials.title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { initials: 'S', avatarBg: 'linear-gradient(135deg,#eda88a,#D96C48)' },
              { initials: 'J', avatarBg: 'linear-gradient(135deg,#c4b5fd,#7c3aed)' },
              { initials: 'A', avatarBg: 'linear-gradient(135deg,#86efac,#16a34a)' },
            ].map((avatar, i) => (
              <div key={i} className="lp-fade bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:-translate-y-1 transition-all duration-200" style={fadeDelay(i * 0.1)}>
                <div className="flex items-center gap-0.5 mb-4">{[...Array(5)].map((_,j) => <StarIcon key={j} />)}</div>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">{T.testimonials.cards[i].text}</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ background: avatar.avatarBg }}>{avatar.initials}</div>
                  <div>
                    <div className="font-semibold text-sm text-gray-900">{T.testimonials.cards[i].name}</div>
                    <div className="text-xs text-gray-400">{T.testimonials.cards[i].role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 lp-fade" style={fade}>
            <div className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full mb-6" style={{ background: '#fae5db', color: '#c4552e' }}>{T.pricing.badge}</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">{T.pricing.title}</h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">{T.pricing.subtitle}</p>
            <div className="flex items-center justify-center gap-3 mt-8">
              <span className={`text-sm ${!isYearly ? 'font-semibold text-gray-700' : 'font-medium text-gray-500'}`}>{T.pricing.monthly}</span>
              <button onClick={() => setIsYearly(!isYearly)} className="relative w-12 h-6 rounded-full focus:outline-none" style={{ background: '#D96C48' }}>
                <span className="block w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform duration-300" style={{ transform: isYearly ? 'translateX(1.5rem)' : 'translateX(0.125rem)' }} />
              </button>
              <span className={`text-sm ${isYearly ? 'font-semibold text-gray-700' : 'font-medium text-gray-500'}`}>
                {T.pricing.yearly}{' '}
                {config.pro_yearly_discount > 0 && (
                  <span className="font-semibold" style={{ color: '#D96C48' }}>
                    ({language === 'es' ? `ahorra ${config.pro_yearly_discount}%` : `save ${config.pro_yearly_discount}%`})
                  </span>
                )}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">

            {/* ── Free ── */}
            <div className="lp-fade bg-white rounded-2xl p-7 border border-gray-200 hover:-translate-y-1 transition-all duration-200 flex flex-col" style={fade}>
              <h3 className="font-bold text-gray-900 text-xl mb-1">{t('pricing.free.title')}</h3>
              <p className="text-sm text-gray-500 mb-6">{t('pricing.free.period')}</p>
              <div className="mb-6">
                <span className="text-4xl font-extrabold text-gray-900">{t('pricing.free.price')}</span>
                <span className="text-gray-500 text-sm ml-1">{T.pricing.perMonth}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {t('pricing.free.features', { recipes: config.free_max_recipes, menus: config.free_max_menus }).map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-gray-600"><CheckMark />{f}</li>
                ))}
              </ul>
              <button onClick={handleFreePlan} className="mt-auto w-full border border-gray-300 text-gray-700 font-semibold py-3 rounded-xl hover:border-orange-400 transition-colors text-sm">
                {user?.plan === 'free' ? (language === 'es' ? 'Plan actual' : 'Current plan') : t('pricing.free.cta')}
              </button>
            </div>

            {/* ── Pro ── */}
            <div className="lp-fade bg-white rounded-2xl p-7 relative flex flex-col" style={{ ...fadeDelay(0.1), boxShadow: '0 0 0 3px #D96C48, 0 24px 48px rgba(217,108,72,0.2)' }}>
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                <span className="text-white text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap" style={{ background: '#D96C48' }}>{T.pricing.mostPopular}</span>
              </div>
              <h3 className="font-bold text-gray-900 text-xl mb-1">{t('pricing.pro.title')}</h3>
              <p className="text-sm text-gray-500 mb-6">{isYearly ? t('pricing.pro.yearlyPeriod') : t('pricing.pro.monthlyPeriod')}</p>
              <div className="mb-6">
                <span className="text-4xl font-extrabold" style={{ color: '#D96C48' }}>
                  {isYearly ? `£${proYearlyPrice.toFixed(2)}` : `£${config.pro_monthly_price.toFixed(2)}`}
                </span>
                <span className="text-gray-500 text-sm ml-1">{T.pricing.perMonth}</span>
                {isYearly && config.pro_yearly_discount > 0 && (
                  <div className="text-xs font-medium mt-1" style={{ color: '#D96C48' }}>
                    {language === 'es' ? `facturado como £${(proYearlyPrice).toFixed(2)} / año` : `billed as £${(proYearlyPrice).toFixed(2)} / year`}
                  </div>
                )}
              </div>
              <ul className="space-y-3 mb-8">
                {t('pricing.pro.features').map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-gray-600"><CheckMark color="#D96C48" />{f}</li>
                ))}
              </ul>
              <button
                onClick={() => isSubscriber && user?.plan !== 'pro' ? handleManageSubscription() : handleSubscribe('pro')}
                disabled={loadingCheckout === 'pro' || user?.plan === 'pro' || managingSubscription}
                className="mt-auto w-full text-white font-bold py-3 rounded-xl hover:opacity-90 transition-all text-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg,#D96C48 0%,#c4552e 100%)' }}
              >
                {loadingCheckout === 'pro' || managingSubscription ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : user?.plan === 'pro' ? (
                  language === 'es' ? 'Plan actual' : 'Current plan'
                ) : isSubscriber ? (
                  language === 'es' ? 'Cambiar Plan' : 'Switch Plan'
                ) : (
                  t('pricing.pro.cta')
                )}
              </button>
            </div>

            {/* ── Premium ── */}
            <div className="lp-fade bg-white rounded-2xl p-7 border border-gray-200 hover:-translate-y-1 transition-all duration-200 flex flex-col" style={fadeDelay(0.2)}>
              <h3 className="font-bold text-gray-900 text-xl mb-1">{t('pricing.premium.title')}</h3>
              <p className="text-sm text-gray-500 mb-6">{isYearly ? t('pricing.premium.yearlyPeriod') : t('pricing.premium.monthlyPeriod')}</p>
              <div className="mb-6">
                <span className="text-4xl font-extrabold text-gray-900">
                  {isYearly ? `£${premiumYearlyPrice.toFixed(2)}` : `£${config.premium_monthly_price.toFixed(2)}`}
                </span>
                <span className="text-gray-500 text-sm ml-1">{T.pricing.perMonth}</span>
                {isYearly && config.premium_yearly_discount > 0 && (
                  <div className="text-xs font-medium mt-1" style={{ color: '#D96C48' }}>
                    {language === 'es' ? `facturado como £${(premiumYearlyPrice).toFixed(2)} / año` : `billed as £${(premiumYearlyPrice).toFixed(2)} / year`}
                  </div>
                )}
              </div>
              <ul className="space-y-3 mb-8">
                {[
                  ...t('pricing.premium.features'),
                  ...(config?.pexels_enabled ? [t('pricing.premium.aiImagesFeature')] : []),
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-gray-600"><CheckMark />{f}</li>
                ))}
              </ul>
              <button
                onClick={() => isSubscriber && user?.plan !== 'premium' ? handleManageSubscription() : handleSubscribe('premium')}
                disabled={loadingCheckout === 'premium' || user?.plan === 'premium' || managingSubscription}
                className="mt-auto w-full border border-gray-300 text-gray-700 font-semibold py-3 rounded-xl hover:border-orange-400 transition-colors text-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loadingCheckout === 'premium' || managingSubscription ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : user?.plan === 'premium' ? (
                  language === 'es' ? 'Plan actual' : 'Current plan'
                ) : isSubscriber ? (
                  language === 'es' ? 'Cambiar Plan' : 'Switch Plan'
                ) : (
                  t('pricing.premium.cta')
                )}
              </button>
            </div>

          </div>
          <p className="text-center text-sm text-gray-400 mt-8">{T.pricing.note}</p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-20 md:py-28 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 lp-fade" style={fade}>
            <div className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full mb-6" style={{ background: '#fae5db', color: '#c4552e' }}>{T.faq.badge}</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">{T.faq.title}</h2>
            <p className="text-gray-500 text-lg">{T.faq.subtitle}</p>
          </div>
          <div className="space-y-3 lp-fade" style={fade}>
            {T.faq.items.map((item) => (
              <details key={item.q} className="bg-white rounded-2xl border border-gray-100 shadow-sm group">
                <summary className="flex items-center justify-between p-5 cursor-pointer font-semibold text-gray-900 text-sm list-none">
                  {item.q}
                  <svg className="w-5 h-5 text-gray-400 flex-shrink-0 transition-transform group-open:rotate-45" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/></svg>
                </summary>
                <div className="px-5 pb-5 text-sm text-gray-500 leading-relaxed">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lp-fade rounded-3xl px-8 py-14 md:px-16 md:py-20 text-center relative overflow-hidden" style={{ ...fade, background: 'linear-gradient(135deg,#D96C48 0%,#c4552e 100%)' }}>
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
            </div>
            <div className="relative z-10">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-5 leading-tight">
                {T.cta.title1}<br className="hidden sm:block" /> {T.cta.title2}
              </h2>
              <p className="text-lg max-w-xl mx-auto mb-10" style={{ color: 'rgba(255,255,255,0.8)' }}>{T.cta.subtitle}</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button onClick={goToCTA} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white font-bold px-10 py-4 rounded-xl text-base shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200" style={{ color: '#D96C48' }}>
                  {T.cta.primaryBtn}
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                </button>
                <button onClick={goToCTA} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 font-semibold px-8 py-4 rounded-xl text-base text-white border hover:bg-white/20 transition-all duration-200" style={{ borderColor: 'rgba(255,255,255,0.3)' }}>
                  {isAuthenticated ? T.cta.secondaryBtnAuth : T.cta.secondaryBtn}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-charcoal text-gray-400 py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center gap-2.5 mb-4">
                <Logo dark />
                <span className="text-white font-bold text-lg">MealPlanner</span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: '#6b7280' }}>{T.footer.tagline}</p>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">{T.footer.product}</h4>
              <ul className="space-y-2.5">
                {[['#features', T.nav.features],['#how-it-works', T.nav.howItWorks],['#pricing', T.nav.pricing],['#faq', T.nav.faq]].map(([href, label]) => (
                  <li key={label}><a href={href} className="text-sm hover:text-white transition-colors" style={{ color: '#6b7280' }}>{label}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">{T.footer.account}</h4>
              <ul className="space-y-2.5">
                <li><button onClick={goToCTA} className="text-sm hover:text-white transition-colors bg-transparent border-none p-0 cursor-pointer" style={{ color: '#6b7280' }}>{T.footer.signUp}</button></li>
                <li><button onClick={goToCTA} className="text-sm hover:text-white transition-colors bg-transparent border-none p-0 cursor-pointer" style={{ color: '#6b7280' }}>{T.footer.login}</button></li>
                <li><button onClick={() => navigate('/pricing')} className="text-sm hover:text-white transition-colors bg-transparent border-none p-0 cursor-pointer" style={{ color: '#6b7280' }}>{T.footer.viewPricing}</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">{T.footer.legal}</h4>
              <ul className="space-y-2.5">
                <li><button onClick={() => navigate('/privacy')} className="text-sm hover:text-white transition-colors bg-transparent border-none p-0 cursor-pointer" style={{ color: '#6b7280' }}>{T.footer.privacy}</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t pt-8 flex flex-col sm:flex-row items-center justify-between gap-4" style={{ borderColor: '#1f2937' }}>
            <p className="text-xs" style={{ color: '#4b5563' }}>{T.footer.copyright}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
